import {
  trackGAEvent,
  createGenericSelectContentEvent,
  EventObject,
  sanitizeStrings,
} from '@moonpig/web-core-analytics'
import { createReminderSuggestionEvent } from './analytics'

export const departmentToCategory = (department?: string) => {
  switch (department?.toLowerCase()) {
    case 'all_gifts':
      return 'gifts'
    case 'all_flowers_and_plants':
      return 'flowers'
    default:
      return 'cards'
  }
}

export const trackClearSearchEvent = () => {
  trackGAEvent({
    event: 'search_input_clear',
    event_data: {
      category: 'search',
      action: 'search input clear',
      label: 'search bar',
      non_interaction: true,
      value: undefined,
    },
  })
}

export const trackSuggestionViewEvent = ({
  searchValue,
  count,
  department,
}: {
  department: string
  searchValue: string
  count: number
}) =>
  trackGAEvent({
    event: 'search_suggestion',

    event_data: {
      category: 'search',
      action: 'suggestions displayed',
      label: 'input',
      non_interaction: true,
      value: count,
    },

    results_data: {
      number_of_results: count,
      product_category: department.toLowerCase(),
      results_list: searchValue.toLowerCase(),
      suggestion_type: undefined,
      input_type: 'keyword',
    },
  })

export const trackSuggestionClickEvent = ({
  suggestion,
  position,
  count,
  department,
  type,
  activeTab,
  suggestionType,
}: {
  suggestion: string
  department: string
  position: number
  count: number
  type: 'navigational' | 'search'
  activeTab?: string
  suggestionType:
    | 'facet suggestion'
    | 'query suggestion'
    | 'navigational suggestion'
}) => {
  const category =
    activeTab || /* istanbul ignore next */ departmentToCategory(department)

  const sanitizedSuggestion = sanitizeStrings(suggestion, {
    invalidCharacters: true,
  })

  trackGAEvent({
    event: 'search_suggestion_click',

    event_data: {
      category: 'search',
      action: 'suggestion clicked',
      label:
        `${sanitizedSuggestion} | ${position}/${count} | ${category}`.toLowerCase(),
      non_interaction: true,
      value: undefined,
    },

    results_data: {
      index: position,
      number_of_results: count,
      product_category: departmentToCategory(department),
      results_list: sanitizedSuggestion.toLowerCase(),
      input_type: suggestionType,
      suggestion_type: type,
    },
  })
}

export const trackSuggestionsTabSelectedEvent = (tab: string) => {
  trackGAEvent(
    createGenericSelectContentEvent({
      eventCategory: 'search action',
      eventAction: 'tab selected',
      eventLabel: tab,
    }),
  )
}

export const trackReminderClickEvent = ({
  numberOfReminders,
  index,
  department,
  occasion,
}: {
  numberOfReminders: number
  index: number
  department: string
  occasion: string
}) => {
  trackGAEvent(
    createReminderSuggestionEvent({
      event: 'search_suggestion_click',
      eventAction: 'suggestion clicked',
      eventLabel:
        `reminders ${occasion} | ${index} / ${numberOfReminders}`.toLowerCase(),
      index,
      numberOfResults: numberOfReminders,
      searchTerm: occasion,
      productCategory: departmentToCategory(department),
    }),
  )
}

export const trackRemindersViewedEvent = ({
  numberOfReminders,
  department,
  trackGAEventOnce,
}: {
  numberOfReminders: number
  department: string
  trackGAEventOnce: (event: EventObject) => void
}) => {
  trackGAEventOnce(
    createReminderSuggestionEvent({
      event: 'search_suggestion',
      eventAction: 'suggestions displayed',
      eventLabel: 'no input',
      eventValue: numberOfReminders,
      numberOfResults: numberOfReminders,
      productCategory: departmentToCategory(department),
    }),
  )
}
